import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type FormEvent,
  type SetStateAction
} from 'react';
import {
  Appointment,
  AddOn,
  Company,
  CompanyUser,
  MedicalCertificate,
  Service,
  SystemConfig,
  Venue
} from '@/types';
import { parseDateWithTime } from '@/lib/dates';
import { buildMapById } from '@/lib/collections';
import { type CompanyDashboardView, type DeleteStep, type TeamMemberDraft } from '../types';
import { useCompanyDashboardTeam } from './useCompanyDashboardTeam';
import { useCompanyDashboardSecurity } from './useCompanyDashboardSecurity';
import { useCompanyDashboardBookings } from './useCompanyDashboardBookings';

interface DashboardParams {
  company: Company;
  appointments: Appointment[];
  services: Service[];
  venues: Venue[];
  addOns: AddOn[];
  certificates: MedicalCertificate[];
  onForceRefresh?: () => Promise<void>;
  systemConfig?: SystemConfig;
}

interface PaginationResult<T> {
  items: T[];
  currentPage: number;
  totalCount: number;
  totalPages: number;
}

interface DashboardBookingRowData {
  appDate: Date | null;
  service: string | undefined;
  venueCity: string | undefined;
  addonsDisplay: string;
  hasCertificate: boolean;
  certificateValidUntil: Date | null;
}

export interface UseCompanyDashboardState {
  view: CompanyDashboardView;
  setView: Dispatch<SetStateAction<CompanyDashboardView>>;

  search: string;
  setSearch: Dispatch<SetStateAction<string>>;

  setBookingsPage: Dispatch<SetStateAction<number>>;
  bookingsPageSize: number;
  setBookingsPageSizeAndReset: (size: number) => void;

  setTeamPage: Dispatch<SetStateAction<number>>;
  teamPageSize: number;
  setTeamPageSizeAndReset: (size: number) => void;

  isPrimaryAccount: boolean;
  companyApps: Appointment[];
  filteredApps: Appointment[];
  activeEmployees: number;
  bookingsPagination: PaginationResult<Appointment>;
  teamMembers: CompanyUser[];
  teamPagination: PaginationResult<CompanyUser>;
  getDashboardBookingRowData: (app: Appointment) => DashboardBookingRowData;
  servicesById: Map<string, Service>;
  venuesById: Map<string, Venue>;
  addOnsById: Map<string, AddOn>;

  isAddingTeamMember: boolean;
  newMember: TeamMemberDraft;
  setNewMember: Dispatch<SetStateAction<TeamMemberDraft>>;
  memberError: string;
  isSavingMember: boolean;
  editingMember: CompanyUser | null;
  setEditingMember: Dispatch<SetStateAction<CompanyUser | null>>;
  teamEditError: string;
  isSavingTeamEdit: boolean;
  isUpdatingPassword: boolean;
  resettingMemberId: string | null;
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
  passwordError: string;
  setOldPassword: Dispatch<SetStateAction<string>>;
  setNewPassword: Dispatch<SetStateAction<string>>;
  setConfirmPassword: Dispatch<SetStateAction<string>>;

  editingBooking: Appointment | null;
  editingBookingOriginal: Appointment | null;
  setEditingBooking: Dispatch<SetStateAction<Appointment | null>>;
  isLoadingEditingAvailability: boolean;
  editingTimeOptions: string[];
  bookingActionError: string;
  isSavingBooking: boolean;

  deleteStep: DeleteStep;
  memberToDelete: CompanyUser | null;

  openAddTeamMember: () => void;
  closeAddTeamMember: () => void;
  handleAddTeamMember: () => Promise<void>;
  closeEditMember: () => void;
  handleSaveTeamMember: () => Promise<void>;
  handleResetMemberPassword: (member: CompanyUser) => Promise<void>;
  handlePasswordUpdate: (event: FormEvent) => Promise<void>;
  openEditBooking: (app: Appointment) => void;
  closeEditBooking: () => void;
  handleSaveBooking: () => Promise<void>;
  handleCancelBooking: () => Promise<void>;
  openDeleteMember: (member: CompanyUser) => void;
  closeDeleteMember: () => void;
  executeDelete: () => Promise<void>;
  handleViewCertificate: (app: Appointment) => void;
  handleDownloadCertificate: (app: Appointment) => Promise<void>;
  downloadingCertificateAppId: string | null;
}

const normalizeSearchValue = (value: string) =>
  String(value || '')
    .trim()
    .toLowerCase();

const paginateItems = <T>(items: T[], page: number, pageSize: number) => {
  const totalCount = items.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.min(page, totalPages);

  return {
    totalCount,
    totalPages,
    currentPage,
    items: items.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  };
};

export const useCompanyDashboard = ({
  company,
  appointments,
  services,
  venues,
  addOns,
  certificates,
  systemConfig,
  onForceRefresh
}: DashboardParams): UseCompanyDashboardState => {
  const [view, setView] = useState<CompanyDashboardView>('dashboard');
  const [search, setSearch] = useState('');
  const [bookingsPage, setBookingsPage] = useState(1);
  const [bookingsPageSize, setBookingsPageSize] = useState(25);

  const {
    isPrimaryAccount,
    teamMembers,
    teamPagination,
    isAddingTeamMember,
    newMember,
    setNewMember,
    memberError,
    isSavingMember,
    editingMember,
    setEditingMember,
    teamEditError,
    isSavingTeamEdit,
    resettingMemberId,
    memberToDelete,
    deleteStep,
    setTeamPage,
    teamPageSize,
    setTeamPageSizeAndReset,
    openAddTeamMember,
    closeAddTeamMember,
    handleAddTeamMember,
    closeEditMember,
    handleSaveTeamMember,
    handleResetMemberPassword,
    openDeleteMember,
    closeDeleteMember,
    executeDelete
  } = useCompanyDashboardTeam({ company, onForceRefresh });
  const {
    oldPassword,
    newPassword,
    confirmPassword,
    passwordError,
    isUpdatingPassword,
    setOldPassword,
    setNewPassword,
    setConfirmPassword,
    handlePasswordUpdate
  } = useCompanyDashboardSecurity({ company });

  const servicesById = useMemo(() => buildMapById(services), [services]);
  const addOnsById = useMemo(() => buildMapById(addOns), [addOns]);
  const venuesById = useMemo(() => buildMapById(venues), [venues]);
  const certificatesById = useMemo(() => buildMapById(certificates), [certificates]);
  const certificatesByAppointmentId = useMemo(
    () =>
      new Map(certificates.map((certificate) => [certificate.appointmentId, certificate] as const)),
    [certificates]
  );

  const {
    editingBooking,
    editingBookingOriginal,
    setEditingBooking,
    isLoadingEditingAvailability,
    editingTimeOptions,
    bookingActionError,
    isSavingBooking,
    openEditBooking,
    closeEditBooking,
    handleSaveBooking,
    handleCancelBooking,
    handleViewCertificate,
    handleDownloadCertificate,
    downloadingCertificateAppId
  } = useCompanyDashboardBookings({
    company,
    servicesById,
    venuesById,
    addOnsById,
    certificatesById,
    systemConfig,
    onForceRefresh
  });

  const normalizedSearch = useMemo(() => normalizeSearchValue(search), [search]);

  const parseAppointmentDate = useCallback(
    (dateValue: string, timeValue: string) => parseDateWithTime(dateValue, timeValue),
    []
  );

  const getBookingAddonNames = useCallback(
    (booking: Appointment) =>
      (booking.addOnIds || [])
        .map((id) => addOnsById.get(id)?.name)
        .filter((name): name is string => Boolean(name))
        .join(', '),
    [addOnsById]
  );

  const companyApps = useMemo(
    () =>
      appointments
        .filter((a) => a.companyId === company.id)
        .sort((a, b) => {
          const aDate = parseAppointmentDate(a.date, a.time);
          const bDate = parseAppointmentDate(b.date, b.time);
          const aTime = aDate ? aDate.getTime() : 0;
          const bTime = bDate ? bDate.getTime() : 0;
          return bTime - aTime;
        }),
    [appointments, company.id, parseAppointmentDate]
  );

  const activeEmployees = useMemo(
    () => Array.from(new Set(companyApps.map((a) => a.customerEmail))).length,
    [companyApps]
  );

  const filteredApps = useMemo(
    () =>
      companyApps.filter(
        (a) =>
          !normalizedSearch ||
          normalizeSearchValue(a.customerName).includes(normalizedSearch) ||
          normalizeSearchValue(a.id).includes(normalizedSearch)
      ),
    [companyApps, normalizedSearch]
  );

  const bookingsPagination = useMemo(
    () => paginateItems(filteredApps, bookingsPage, bookingsPageSize),
    [filteredApps, bookingsPage, bookingsPageSize]
  );

  const getDashboardBookingRowData = useCallback(
    (app: Appointment): DashboardBookingRowData => {
      const service = servicesById.get(app.serviceId);
      const venue = venuesById.get(app.venueId);
      const cert = certificatesByAppointmentId.get(app.id);

      return {
        appDate: parseAppointmentDate(app.date, app.time),
        service: service?.name,
        venueCity: venue?.city,
        addonsDisplay: getBookingAddonNames(app),
        hasCertificate: Boolean(cert),
        certificateValidUntil: cert ? new Date(cert.validUntil) : null
      };
    },
    [
      servicesById,
      venuesById,
      certificatesByAppointmentId,
      parseAppointmentDate,
      getBookingAddonNames
    ]
  );

  useEffect(() => {
    setBookingsPage(1);
  }, [search]);

  const setBookingsPageSizeAndReset = useCallback((size: number) => {
    setBookingsPageSize(size);
    setBookingsPage(1);
  }, []);

  return {
    view,
    setView,
    search,
    setSearch,
    setBookingsPage,
    bookingsPageSize,
    setBookingsPageSizeAndReset,
    setTeamPage,
    teamPageSize,
    setTeamPageSizeAndReset,
    isPrimaryAccount,
    companyApps,
    filteredApps,
    activeEmployees,
    bookingsPagination,
    teamMembers,
    teamPagination,
    getDashboardBookingRowData,
    servicesById,
    venuesById,
    addOnsById,
    isAddingTeamMember,
    newMember,
    setNewMember,
    memberError,
    isSavingMember,
    editingMember,
    setEditingMember,
    teamEditError,
    isSavingTeamEdit,
    isUpdatingPassword,
    resettingMemberId,
    oldPassword,
    newPassword,
    confirmPassword,
    passwordError,
    setOldPassword,
    setNewPassword,
    setConfirmPassword,
    editingBooking,
    editingBookingOriginal,
    setEditingBooking,
    isLoadingEditingAvailability,
    editingTimeOptions,
    bookingActionError,
    isSavingBooking,
    deleteStep,
    memberToDelete,
    openAddTeamMember,
    closeAddTeamMember,
    handleAddTeamMember,
    closeEditMember,
    handleSaveTeamMember,
    handleResetMemberPassword,
    handlePasswordUpdate,
    openEditBooking,
    closeEditBooking,
    handleSaveBooking,
    handleCancelBooking,
    openDeleteMember,
    closeDeleteMember,
    executeDelete,
    handleViewCertificate,
    handleDownloadCertificate,
    downloadingCertificateAppId
  };
};
